"use client";

import React, { useState } from "react";
import { Dialog, DialogContent, DialogTrigger, DialogTitle, DialogDescription, DialogHeader, DialogClose } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ZoomIn, X } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const galleryItems = [{
  id: 1,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1559261/img/clinic_reception_modern_soft_lighting.png",
  alt: "Recepção moderna e acolhedora",
  category: "Ambiente",
  title: "Recepção Acolhedora",
  description: "Um espaço projetado para o seu conforto e bem-estar desde o primeiro momento.",
  height: "h-64"
}, {
  id: 2,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1559261/img/tricocope_high_tech_equipment_closeup.png",
  alt: "Equipamento de tricoscopia de alta precisão",
  category: "Tecnologia",
  title: "Tricoscopia Digital",
  description: "Tecnologia avançada para análise detalhada do couro cabeludo e fios.",
  height: "h-96"
}, {
  id: 3,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1559261/img/treatment_room_comfortable_chair_clean.png",
  alt: "Sala de tratamento com cadeira confortável",
  category: "Ambiente",
  title: "Salas de Tratamento",
  description: "Privacidade e conforto para a realização dos seus protocolos capilares.",
  height: "h-72"
}, {
  id: 4,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1559261/img/microscope_hair_analysis_screen_data.png",
  alt: "Tela com análise de dados capilares",
  category: "Tecnologia",
  title: "Análise de Dados",
  description: "Diagnósticos baseados em evidências com visualização microscópica em tempo real.",
  height: "h-64"
}, {
  id: 5,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1559261/img/waiting_area_plants_calm_atmosphere.png",
  alt: "Área de espera com plantas e iluminação suave",
  category: "Ambiente",
  title: "Bem-estar e Relaxamento",
  description: "Um ambiente calmo que integra elementos naturais para reduzir o stress.",
  height: "h-80"
}, {
  id: 6,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1559261/img/specialist_using_scanner_on_patient.png",
  alt: "Especialista utilizando scanner capilar",
  category: "Tecnologia",
  title: "Precisão no Diagnóstico",
  description: "Nossa equipe utiliza as ferramentas mais modernas para garantir resultados precisos.",
  height: "h-72"
}];
export default function EnvironmentAndTechnology() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="ambiente-e-tecnologia" ref={ref} className={cn("py-24 bg-background relative overflow-hidden transition-opacity duration-1000", isVisible ? "opacity-100" : "opacity-0")}>
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] rounded-full bg-primary/5 blur-[100px]" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[400px] h-[400px] rounded-full bg-secondary/5 blur-[80px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-primary mb-6">
            Ambiente e Tecnologia
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Na Dil Saúde Capilar, unimos a precisão da ciência com o conforto de um spa. 
            Nossa estrutura foi pensada para oferecer a melhor experiência, aliando equipamentos 
            de última geração a um ambiente acolhedor e sofisticado.
          </p>
        </div>

        <div className="columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6">
          {galleryItems.map((item, i) => <div key={item.id} data-index={i} className="break-inside-avoid">
              <Dialog>
                <DialogTrigger asChild>
                  <div className="group relative cursor-pointer overflow-hidden rounded-[var(--radius)] shadow-md hover:shadow-xl transition-all duration-500 bg-card border border-border/50">
                    <div className={cn("relative w-full overflow-hidden", item.height)}>
                      <img src={item.src} alt={item.alt} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                      <div className="absolute inset-0 bg-primary/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center justify-center backdrop-blur-[2px]">
                        <div className="bg-background/90 p-3 rounded-full shadow-lg transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                          <ZoomIn className="w-6 h-6 text-primary" />
                        </div>
                      </div>
                    </div>
                    <div className="p-5 bg-card relative z-20">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-bold tracking-wider text-primary uppercase bg-primary/10 px-2 py-1 rounded-full">
                          {item.category}
                        </span>
                      </div>
                      <h3 className="text-lg font-semibold text-foreground mb-1 group-hover:text-primary transition-colors">
                        {item.title}
                      </h3>
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </DialogTrigger>
                
                <DialogContent className="max-w-4xl w-[95vw] p-0 overflow-hidden bg-transparent border-none shadow-none sm:max-w-5xl">
                  <div className="relative bg-card rounded-xl overflow-hidden shadow-2xl border border-border">
                    <DialogHeader className="sr-only">
                      <DialogTitle>{item.title}</DialogTitle>
                      <DialogDescription>{item.description}</DialogDescription>
                    </DialogHeader>
                    
                    <div className="relative aspect-video w-full bg-black/5">
                      <img src={item.src} alt={item.alt} className="w-full h-full object-contain" />
                    </div>
                    
                    <div className="p-6 bg-card border-t border-border">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div>
                          <h3 className="text-2xl font-serif font-bold text-primary mb-2">
                            {item.title}
                          </h3>
                          <p className="text-muted-foreground">
                            {item.description}
                          </p>
                        </div>
                        <DialogClose asChild>
                          <Button variant="outline" className="shrink-0">
                            Fechar
                          </Button>
                        </DialogClose>
                      </div>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>)}
        </div>
      </div>
    </section>;
}